import loader from '../partials/loader'

const stockHandler = (productGeneric) => ({
    ...loader,
    expressStockAvailable: false,
    expressStock: {
        quantity: 0,
        cutOff: {
            hours: 0,
            minutes: 0,
            seconds: 0,
            today: false,
        },
    },
    additionalStockAvailable: false,
    additionalStock: {
        secondary: {
            quantity: 0,
            lead: null,
        },
        external: {
            quantity: 0,
            lead: null,
        },
    },
    maxStock: 0,
    maxLead: null,
    secondaryStockLeadTime: null,
    isDiscontinued: false,
    isAvailableForOrder: null,
    productId: -1,
    limitedStockLevel: 0,
    leadtimesFetching: false,
    fallbackLeadTimes: {
        external: null,
        secondary: null,
    },
    // Quickest lead time of the stock that can be ordered, null until the lead times have loaded
    get soonestLead() {
        const leads = [this.additionalStock.external, this.additionalStock.secondary]
            .filter((stock) => stock.quantity > 0 && stock.lead !== null)
            .map((stock) => stock.lead)

        return leads.length ? Math.min(...leads) : null
    },
    init() {
        this.startLoader()

        if (productGeneric) {
            this.productId = productGeneric
        }

        // Bit of a hack but since we only have this module loaded on
        // the product details page, its a convenient place to add.
        const showQuoteModalOnLoad = new URLSearchParams(window.location.search).get('quote')
        if (showQuoteModalOnLoad == 1) {
            Alpine.store('general').showOverlay = true
            Alpine.store('general').showGetQuote = true
        }
    },
    updateStock($event) {
        if ($event.detail.id_product != this.productId) {
            return
        }

        if ($event.detail.express_cutoff < 0) {
            $event.detail.express_cutoff += 60 * 24
        }

        this.isDiscontinued = $event.detail.date_discontinued !== null
        this.isAvailableForOrder = $event.detail.available_for_order === 1
        this.expressStockAvailable = $event.detail.quantity > 0 && $event.detail.quantity >= $event.detail.minimal_quantity;
        this.additionalStockAvailable = $event.detail.ex_stock > 0 || $event.detail.secondary_stock > 0
        this.maxStock = Math.max($event.detail.quantity, $event.detail.ex_stock, $event.detail.secondary_stock)
        this.maxLead = null
        this.limitedStockLevel = $event.detail.limited_stock_level

        if (this.expressStockAvailable) {
            this.expressStock.cutOff.hours = parseInt($event.detail.express_cutoff / 60, 10)
            this.expressStock.cutOff.minutes = parseInt($event.detail.express_cutoff % 60, 10)
            this.expressStock.quantity = $event.detail.quantity;

            if (this.expressStock.cutOff.hours === 0 && this.expressStock.cutOff.minutes === 0) {
                this.expressStock.cutOff.seconds = 60 - new Date().getSeconds()
            }

            const now = new Date()
            const midnight = new Date()
            midnight.setHours(24, 0, 0, 0)
            const minutesUntilMidnight = (midnight - now) / 1000 / 60
            this.expressStock.cutOff.today = $event.detail.express_cutoff < minutesUntilMidnight
        }

        this.additionalStock.secondary.quantity = $event.detail.secondary_stock
        this.additionalStock.secondary.lead = null  // Hidden until API completes

        this.additionalStock.external.quantity = $event.detail.ex_stock
        this.additionalStock.external.lead = null  // Hidden until API completes

        this.secondaryStockLeadTime = null  // Hidden until API completes

        // Store database values as fallback in case API fails
        this.fallbackLeadTimes.external = $event.detail.ex_lead_time
        this.fallbackLeadTimes.secondary = $event.detail.secondary_lead_time

        // Fetch dynamic leadtimes
        if (!this.leadtimesFetching) {
            this.fetchDynamicLeadtimes($event.detail)
        }

        this.stopLoader()
    },
    async fetchDynamicLeadtimes(stockData) {
        this.leadtimesFetching = true

        // Fetch dynamic leadtime for express stock if available
        if (stockData.quantity > 0 && this.expressStockAvailable) {
            const expressLeadtime = await window.api.getDynamicLeadtime(
                this.productId,
                stockData.quantity
            )
            if (expressLeadtime && expressLeadtime.days !== undefined) {
                // Update cutOff.today based on dynamic leadtime
                // 0 days = ships today, 1+ days = ships tomorrow
                this.expressStock.cutOff.today = expressLeadtime.days === 0
            }
        }

        // Fetch dynamic leadtime for external stock if available
        if (stockData.ex_stock > 0) {
            const exLeadtime = await window.api.getDynamicLeadtime(
                this.productId,
                stockData.ex_stock
            )
            if (exLeadtime && exLeadtime.days !== undefined) {
                this.additionalStock.external.lead = exLeadtime.days
            } else {
                // API failed, use database fallback
                this.additionalStock.external.lead = this.fallbackLeadTimes.external
            }
        }

        // Fetch dynamic leadtime for secondary stock if available
        if (stockData.secondary_stock > 0) {
            const secondaryLeadtime = await window.api.getDynamicLeadtime(
                this.productId,
                stockData.secondary_stock
            )
            if (secondaryLeadtime && secondaryLeadtime.days !== undefined) {
                this.additionalStock.secondary.lead = secondaryLeadtime.days
                this.secondaryStockLeadTime = secondaryLeadtime.days
            } else {
                // API failed, use database fallback
                this.additionalStock.secondary.lead = this.fallbackLeadTimes.secondary
                this.secondaryStockLeadTime = this.fallbackLeadTimes.secondary
            }
        }

        // Fetch dynamic leadtime for manufactured items (no stock available)
        if (stockData.quantity < stockData.minimal_quantity && !stockData.ex_stock && !stockData.secondary_stock) {
            // Use minimal quantity or 1000000 for the API call to guarantee secondary stock is returned
            const manufacturingQuantity = stockData.minimal_quantity || 1000000
            const manufacturedLeadtime = await window.api.getDynamicLeadtime(
                this.productId,
                manufacturingQuantity
            )
            if (manufacturedLeadtime && manufacturedLeadtime.days !== undefined) {
                this.secondaryStockLeadTime = manufacturedLeadtime.days
            } else {
                // API failed, use database fallback
                this.secondaryStockLeadTime = this.fallbackLeadTimes.secondary
            }
        }

        // Update maxLead after all API calls complete
        const externalLead = this.additionalStock.external.lead ?? 0
        const secondaryLead = this.additionalStock.secondary.lead ?? 0
        this.maxLead = Math.max(externalLead, secondaryLead)

        this.leadtimesFetching = false
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('stockHandler', stockHandler)
})
